import { useQuery, useQueries } from "@tanstack/react-query";
import { useMemo } from "react";
import { useToast } from "@repo/ui";
import type { Field } from "../types.js";
import type { ConfigurationApi } from "./shared-api/shared-api-context.js";
import { useSharedApi } from "./shared-api/use-shared-api.js";

export const getWorkflowFieldsQueryKey = (
  configurationApi: ConfigurationApi,
  workflowId: number
) =>
  configurationApi.queryOptions("get", "/api/workflow/{workflowId}/Field", {
    params: { path: { workflowId } },
  }).queryKey;

const fetchWorkflowFields = async (
  configurationApi: ConfigurationApi,
  workflowId: number
): Promise<Field[]> => {
  const response = await configurationApi.client.GET("/api/workflow/{workflowId}/Field", {
    params: { path: { workflowId } },
  });

  if (response.error) {
    throw new Error(
      `Failed to fetch workflow fields for workflow ${workflowId}: ${response.error.toString()}`
    );
  }

  return response.data || [];
};

export const useWorkflowFields = (workflowId: number | undefined, enabled = true) => {
  const { configurationApi } = useSharedApi();
  const { showToast } = useToast();
  const { data, isLoading, error, refetch } = useQuery<Field[]>({
    queryKey: workflowId
      ? getWorkflowFieldsQueryKey(configurationApi, workflowId)
      : ["workflow-fields", "none"],
    queryFn: async () => {
      if (!workflowId) return [];

      try {
        return await fetchWorkflowFields(configurationApi, workflowId);
      } catch (err) {
        const errorMessage = (err as Error).message;
        showToast("error", "Error", errorMessage);
        throw err;
      }
    },
    enabled: enabled && !!workflowId,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  return {
    fields: data || [],
    isLoading,
    error,
    refetch,
  };
};

export const useMultipleWorkflowFields = (workflowIds: number[]) => {
  const { configurationApi } = useSharedApi();
  const { showToast } = useToast();

  const queryConfigs = useMemo(
    () =>
      workflowIds.map((workflowId) => ({
        queryKey: getWorkflowFieldsQueryKey(configurationApi, workflowId),
        queryFn: () => fetchWorkflowFields(configurationApi, workflowId),
        staleTime: 2 * 60 * 1000,
        gcTime: 5 * 60 * 1000,
      })),
    [workflowIds, configurationApi]
  );

  const queries = useQueries({ queries: queryConfigs });

  const workflowFieldsMap = useMemo(() => {
    const map = new Map<number, Field[]>();

    workflowIds.forEach((workflowId, index) => {
      const query = queries[index];
      if (query?.data) {
        map.set(workflowId, query.data);
      }
    });

    return map;
  }, [workflowIds, queries]);

  const isLoading = queries.some((query) => query.isLoading);
  const hasError = queries.some((query) => query.isError);

  const isReady = useMemo(
    () => workflowIds.length > 0 && workflowIds.every((id) => workflowFieldsMap.has(id)),
    [workflowIds, workflowFieldsMap]
  );

  if (hasError) {
    queries.forEach((query, index) => {
      if (query.isError) {
        const workflowId = workflowIds[index];
        const errorMessage = (query.error as Error)?.message || "Unknown error";
        showToast(
          "error",
          "Error",
          `Failed to fetch workflow fields for workflow ${workflowId}: ${errorMessage}`
        );
      }
    });
  }

  return {
    workflowFieldsMap,
    isLoading,
    isReady,
    hasError,
    queries,
  };
};
